import React, { useState, useEffect } from "react";

import { UseQueryResult } from "@tanstack/react-query";
import ShipeeziNewGrid, {
  Column,
  RowActionsExtra,
  Constants as GridConstants,
  GeneralActions,
  Category,
  GridProps,
} from "Components/AppGrid/shipeezi-new-grid";
import _ from "lodash";
import { useTranslation } from "react-i18next";
import { BiTrash } from "react-icons/bi";
import { FiMessageSquare } from "react-icons/fi";

import { useAppState } from "../../../../context/StateContext";
import useActionComment from "../../../../hooks/useActionComment";
import { CartonProduct } from "../../../../models/carton.product";
import { Comment } from "../../../../models/comment";
import { Currency } from "../../../../models/currency";
import { Port } from "../../../../models/port";
import { Product } from "../../../../models/product";
import { PurchaseOrder, PurchaseOrderProduct, ValueOf } from "../../../../models/purchase.order";
import { Searches, Filters } from "../../../../models/shipeezi.table";
import { handleGetConvertValue } from "../../../../util/converts";
import { OrderProductStatus, OrderStatus, SearchProducts } from "../../../../util/enums";
import { DecimalFormatType } from "../../../../util/formatDecimalValue";
import { getMeasurement, getWeightMeasurement } from "../../../../util/getMeasurement";
import { paginate } from "../../../../util/paginate";
import Input from "../../../Form/Input";
import CommentsModal from "../../../Generic/Comment/Modal";
import { GridQuantity } from "../../../GridQuantity";
import ModalAddProduct from "./ModalAddProduct";
import ModalEditProduct from "./ModalEditProduct";
import SkeletonProducts from "./skeleton";
import StatusBadge from "./StatusBadge";
import useStyles from "./styles";

interface ProductModalProps {
  modalView?: boolean;
  selecteds?: number[];
  handleSelect?: (ids: number[]) => void;
  keyExtractor?: GridProps["keyExtractor"];
}
interface OrderProductsProps {
  products: PurchaseOrderProduct[];
  overrideColumns?: Column[];
  updateOrder: (attribute: keyof PurchaseOrder, value: ValueOf<PurchaseOrder> | null) => void;
  viewOnly: boolean;
  supplierView: boolean;
  portCodes?: Port[];
  loadingPorts?: boolean;
  currenciesResponse?: UseQueryResult<Currency[], unknown>;
  modalProps?: ProductModalProps;
  purchaseOrder?: PurchaseOrder;
  openedFromView?: boolean;
}

interface SearchAttribute {
  attribute: string;
  values: string[];
}

const OrderProducts = ({
  products,
  overrideColumns,
  updateOrder,
  viewOnly = false,
  supplierView = false,
  currenciesResponse,
  modalProps,
  purchaseOrder,
  portCodes,
  loadingPorts,
  openedFromView,
}: OrderProductsProps) => {
  const classes = useStyles();
  const { t: translate } = useTranslation();
  const { currentUser } = useAppState();

  const measurement = getMeasurement();
  const measurementWeight = getWeightMeasurement();

  const [productToComment, setProductToComment] = useState<PurchaseOrderProduct>();

  const [searchAttributes, setSearchAttributes] = useState<SearchAttribute[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<PurchaseOrderProduct[]>([]);
  const [totalItems, setTotalItems] = useState<number>(0);

  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(10);

  const [selectedProduct, setSelectedProduct] = useState<PurchaseOrderProduct>();
  const [showAddProductModal, setShowAddProductModal] = useState<boolean>(false);
  const [showEditProductModal, setShowEditProductModal] = useState<boolean>(false);

  const [showCommentDialog, setShowCommentDialog] = useState<boolean>(false);
  const [comments, setComments] = useState<Comment[]>([]);
  const [countComments, setCountComments] = useState<number>(0);
  const [commentsPage, setCommentsPage] = useState<number>(0);
  const commentsPageSize = 3;

  const [productView, setProductView] = useState<boolean>(false);

  const { getAllComments, useCreateComment } = useActionComment();

  const { data: dataComments, isFetching: isFetchingComments } = getAllComments(
    [["createdAt", "DESC"]],
    productToComment?.id,
    "purchase_order_product",
    commentsPage,
    commentsPageSize,
    `${productToComment?.purchaseOrderId}`
  );

  useEffect(() => {
    if (dataComments) {
      setComments((state) => [...state, ...dataComments.rows]);
      setCountComments(dataComments.count);
    }
    if (!showCommentDialog) {
      setProductToComment(undefined);
      setComments([]);
      setCommentsPage(0);
      setCountComments(0);
    }
  }, [dataComments, showCommentDialog]);

  const {
    data: createCommentResponse,
    mutate: createComment,
    isLoading: isLoadingCreateComment,
    isSuccess: isSuccessCreateComment,
  } = useCreateComment();

  useEffect(() => {
    if (isSuccessCreateComment) {
      const { data: comment } = createCommentResponse;

      setComments((state) => [{ ...comment, user: { ...currentUser } }, ...state]);
      setCountComments((state) => state + 1);
    }
  }, [isSuccessCreateComment]);

  const filterProducts = (searchAttributes: SearchAttribute[]) => {
    return searchAttributes.reduce((filteredProducts, searchAttribute) => {
      return filteredProducts.filter((purchaseOrderProduct: any) =>
        searchAttribute.values.some((value) => {
          switch (searchAttribute.attribute) {
            case SearchProducts.STATUS:
              return String(purchaseOrderProduct[searchAttribute.attribute])
                ?.toLowerCase()
                .includes(value.toLowerCase());
            case SearchProducts.REFERENCE:
              return String(purchaseOrderProduct.purchaseOrder[searchAttribute.attribute])
                ?.toLowerCase()
                .includes(value.toLowerCase());
            case SearchProducts.DESCRIPTION:
              return (
                String(purchaseOrderProduct[searchAttribute.attribute])
                  ?.toLowerCase()
                  .includes(value.toLowerCase()) ||
                String(purchaseOrderProduct.product[searchAttribute.attribute])
                  ?.toLowerCase()
                  .includes(value.toLowerCase())
              );

            case SearchProducts.COLOR_DESCRIPTION:
              return String(purchaseOrderProduct[searchAttribute.attribute])
                ?.toLowerCase()
                .includes(value.toLowerCase());
            default:
              return String(purchaseOrderProduct.product[searchAttribute.attribute])
                ?.toLowerCase()
                .includes(value.toLowerCase());
          }
        })
      );
    }, products);
  };

  useEffect(() => {
    if (searchAttributes.length > 0) {
      const filteredProducts = filterProducts(searchAttributes);

      setFilteredProducts(paginate(filteredProducts, page, pageSize));
      setTotalItems(filteredProducts.length);
    } else {
      setFilteredProducts(paginate(products, page, pageSize));
      setTotalItems(products.length);
    }
  }, [page, pageSize, searchAttributes, products]);

  const handleCurrentPage = (numPage: number) => {
    setPage(numPage - 1);
  };

  const handlePageSize = (pageSize: number) => {
    setPageSize(pageSize);
  };

  const handleFilter = (filters: Filters) => {
    const attributes: SearchAttribute[] = [];

    if (filters) {
      Object.getOwnPropertyNames(filters).forEach((attribute) => {
        attributes.push({
          attribute,
          values: filters[attribute],
        });
      });
    }
    setSearchAttributes(attributes);
  };

  const handleSearch = (searches: Searches) => {
    const attributes: SearchAttribute[] = [];

    if (searches) {
      searches.forEach((search) => {
        Object.getOwnPropertyNames(search).forEach((attribute) => {
          attributes.push({
            attribute,
            values: [search[attribute]],
          });
        });
      });
    }

    setSearchAttributes(attributes);
  };

  const getTotalQuantityRequested = () => {
    return products.reduce(
      (total: number, product: PurchaseOrderProduct) => (total += product.quantity ?? 0),
      0
    );
  };

  const editProduct = (id: string | number) => {
    const product = products?.find((product) => product.productId === Number(id));

    setSelectedProduct(product);
    setShowEditProductModal(true);
  };

  const viewProduct = (id: string | number) => {
    const product = products?.find((product) => product.productId === Number(id));

    setSelectedProduct(product);
    setProductView(true);
    setShowEditProductModal(true);
  };
  const deleteProduct = (id: number) => {
    updateOrder(
      "purchaseOrderProducts",
      products.filter((product) => String(product.productId) !== String(id))
    );
  };

  const commentProduct = (purchaseOrderProduct: PurchaseOrderProduct) => {
    setProductToComment(purchaseOrderProduct);
    setShowCommentDialog(true);
  };

  const renderQuantityRequested = (value: number) => {
    const productIndex = products.findIndex((product) => product.productId === value);
    const quantity = productIndex === -1 ? 0 : products[productIndex]?.quantity;
    const isAssigned = purchaseOrder?.status === OrderStatus.ASSIGNED;

    if (supplierView) return <span>{quantity}</span>;

    const handleChangeQuantityRequested = (
      event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
      const productsAux = products;

      if (productsAux[productIndex]) {
        productsAux[productIndex].quantity = Number(event.target.value);

        productsAux[productIndex].status =
          productsAux[productIndex].quantity <= (productsAux[productIndex]?.quantityFullfilled || 0)
            ? OrderProductStatus.SCANNED
            : OrderProductStatus.TO_BE_PICKED;

        updateOrder("purchaseOrderProducts", productsAux);
      }
    };

    return (
      <div className={classes.quantityDiv}>
        <Input
          data-cy="quantity-input"
          className={classes.quantityInput}
          type="number"
          min="0"
          value={String(quantity)}
          onChange={handleChangeQuantityRequested}
          disabled={viewOnly || supplierView || isAssigned}
        />
      </div>
    );
  };

  const renderStatus = (value: string) => <StatusBadge value={value} />;

  const formatCurrencyValue = (product: PurchaseOrderProduct, value: number) => {
    return `${product?.currency?.symbol || "$"} ${value} ${product?.currency?.code || ""}`;
  };

  const renderPurchasePrice = (id: number) => {
    const purchaseOrderProduct = products.find((product) => product.productId === Number(id));

    if (!purchaseOrderProduct) return "";

    return formatCurrencyValue(purchaseOrderProduct, purchaseOrderProduct?.purchasePrice || 0);
  };

  const renderTotalPrice = (id: number) => {
    const purchaseOrderProduct = products.find((product) => product.productId === Number(id));

    if (!purchaseOrderProduct) return "";

    const totalPrice =
      (purchaseOrderProduct?.purchasePrice || 0) * (purchaseOrderProduct?.quantity || 0);

    return formatCurrencyValue(purchaseOrderProduct, Number(totalPrice.toFixed(2)));
  };

  const getHSCODEFromCartonProduct = (cartonProducts: CartonProduct[]) => {
    return cartonProducts?.find((cartonProduct) => !cartonProduct.carton?.isCancelled)?.hsCode;
  };

  const columns: Column[] = [
    {
      name: "product",
      title: `${translate("sku")} #`,
      formatText: (product: Product) => product?.sku,
      searchIdentifier: "sku",
      style: {
        minWidth: "85px",
        paddingLeft: "5px",
      },
    },
    {
      name: "description",
      title: translate("productName"),
      formatComponent(value: string, row: PurchaseOrderProduct) {
        if (value) {
          return value;
        }

        if (row?.product && row?.product?.description) {
          return row?.product?.description;
        }

        return "";
      },
      searchIdentifier: "description",
      style: {
        minWidth: "145px",
      },
    },
    {
      name: "productId",
      title: translate("quantityRequested"),
      formatComponent: renderQuantityRequested,
      hideSearch: true,
    },
    {
      name: "quantityFullfilled",
      title: translate("quantityFullfilled"),
      hideSearch: true,
    },
    {
      name: "bookedGrossWeight",
      title: `${translate("weight")} (${(measurementWeight?.code || "")?.toUpperCase()})`,
      formatText: (value: number) =>
        handleGetConvertValue(value || 0, measurementWeight?.code, DecimalFormatType.MAERSK),
      hideSearch: true,
      searchIdentifier: "bookedGrossWeight",
      style: {
        minWidth: "80px",
      },
    },
    {
      name: "bookedMeasurement",
      title: `${translate("volume")} (${(measurement?.code || "")?.toUpperCase()})`,
      formatText: (value: number) =>
        handleGetConvertValue(value || 0, measurement?.code, DecimalFormatType.MAERSK),
      hideSearch: true,
      style: {
        minWidth: "80px",
      },
    },
    // TODO Leonardo - Check what HSCODE should be shown here
    {
      name: "cartonProducts",
      title: translate("hscode"),
      formatText: getHSCODEFromCartonProduct,
      hideSearch: true,
      hideSort: true,
      style: {
        paddingRight: "5px",
      },
    },
    {
      name: "productId",
      title: translate("unitPrice"),
      formatText: renderPurchasePrice,
      hideSearch: true,
      hideSort: true,
      style: {
        minWidth: "70px",
        paddingLeft: "5px",
      },
    },
    {
      name: "productId",
      title: translate("totalPrice"),
      formatText: renderTotalPrice,
      hideSearch: true,
      hideSort: true,
      style: {
        minWidth: "70px",
        paddingLeft: "5px",
      },
    },
    {
      name: "status",
      title: translate("status"),
      formatComponent: renderStatus,
      hideSearch: true,
    },
  ];

  const filterCategories: Category[] = [
    {
      name: translate("status"),
      options: OrderProductStatus && Object.values(OrderProductStatus),
    },
  ];

  const renderProductQuantity = (
    <GridQuantity
      small={!!openedFromView}
      title={translate("quantityOrdered")}
      value={getTotalQuantityRequested()}
      valueProps={{ "data-cy": "quantityOrdered" }}
    />
  );

  const newRecord = () => {
    if (viewOnly || supplierView || modalProps?.modalView) return undefined;

    return {
      text: translate("addProduct"),
      action: () => setShowAddProductModal(true),
      tooltip: translate("addProduct"),
    };
  };

  const generalActions: GeneralActions = {
    orderBy: {
      right: !viewOnly ? ["handleNewRecord"] : ["renderQuantity"],
      left: !viewOnly ? ["handleSearch", "handleFilterBy", "renderQuantity"] : undefined,
    },
    handleNewRecord: newRecord(),
    handleFilterBy: {
      categories: filterCategories,
      onChange: handleFilter,
    },
    handleSearch: {
      onSearch: handleSearch,
    },
    renderQuantity: !modalProps?.modalView && renderProductQuantity,
  };

  const rowActionsExtra: RowActionsExtra[] = [
    {
      title: translate("saveAComment"),
      buttonAndAction: (id: number) => {
        const purchaseOrderProduct = products.find((product) => product.productId === Number(id));

        return purchaseOrderProduct?.id ? (
          <FiMessageSquare
            data-cy="action-comment"
            className={classes.actionExtraIcon}
            size={GridConstants.defaultRowActionSize}
            onClick={() => commentProduct(purchaseOrderProduct)}
          />
        ) : null;
      },
    },
    ...(!supplierView
      ? [
          {
            title: translate("removeProduct"),
            buttonAndAction: (id: number) => (
              <BiTrash
                data-cy="action-remove"
                className={classes.actionExtraIcon}
                size={GridConstants.defaultRowActionSize}
                color="#F97066"
                onClick={() => deleteProduct(id)}
              />
            ),
          },
        ]
      : []),
  ];

  const renderAddProductModal = () => {
    if (!showAddProductModal) return;

    const addProducts = (newProducts: Product[]) => {
      const newProductsAux: PurchaseOrderProduct[] = [];
      const nextValue: number =
        products && products.length ? Number(products[products.length - 1]?.lineId || 0) : 0;

      newProducts.forEach((product: Product, index: number) => {
        newProductsAux.push({
          description: product.description ?? "",
          productId: product.id ?? 0,
          quantity: 0,
          status: "To be picked",
          product: {
            sku: product.sku ?? "",
            description: product.description ?? "",
          },
          lineId: `${nextValue + index + 1}`,
        } as PurchaseOrderProduct);
      });

      updateOrder("purchaseOrderProducts", [...products, ...newProductsAux]);
      setShowAddProductModal(false);
    };

    const addedProducts = () => {
      return products.map((product: PurchaseOrderProduct) => product.productId || 0) || [];
    };

    return (
      <ModalAddProduct
        showAddProductModal={showAddProductModal}
        setShowAddProductModal={setShowAddProductModal}
        addProducts={addProducts}
        addedProducts={addedProducts()}
      />
    );
  };

  const renderEditProductModal = () => {
    if (!showEditProductModal || !selectedProduct) return;

    const handleEditProduct = (product: PurchaseOrderProduct) => {
      const productsAux = _.cloneDeep(products);
      const index = productsAux.findIndex(
        (productAux) => productAux.productId === product.productId
      );

      if (index !== -1) {
        productsAux[index] = product;
        updateOrder("purchaseOrderProducts", productsAux);
      }

      setShowEditProductModal(false);
    };

    return (
      <ModalEditProduct
        handleClose={() => {
          setShowEditProductModal(false);
          setProductView(false);
        }}
        purchaseOrderProduct={selectedProduct}
        portCodes={portCodes}
        loadingPorts={loadingPorts}
        currenciesResponse={currenciesResponse}
        editProduct={handleEditProduct}
        viewOnly={productView}
        supplierView={supplierView}
      />
    );
  };

  const renderCommentDialog = () => {
    if (!showCommentDialog) return;

    const closeCommentsDialog = () => {
      setShowCommentDialog(false);
    };

    const handleLoadMore = () => {
      setCommentsPage((state) => state + 1);
    };

    const handleSubmitComment = (comment: Comment) => {
      createComment({
        ...comment,
        originId: productToComment?.id,
        originTable: "purchase_order_product",
        secondaryOriginId: `${productToComment?.purchaseOrderId}`,
      });
    };

    const hasMoreComments = countComments > (commentsPage + 1) * commentsPageSize;
    const sku = productToComment?.product?.sku;

    return (
      <CommentsModal
        comments={comments}
        count={countComments}
        hasMore={hasMoreComments}
        isLoadingCreate={isLoadingCreateComment}
        isLoadingHistory={isFetchingComments}
        onClose={closeCommentsDialog}
        onLoadMore={handleLoadMore}
        onSubmit={handleSubmitComment}
        open={showCommentDialog}
        placeholderText={`${translate("writeACommentInSKU")} ${sku}`}
      />
    );
  };

  return (
    <div className={classes.mainDiv}>
      <div data-cy="orderProductsListGrid" className={classes.grid}>
        <ShipeeziNewGrid
          keyExtractor={
            modalProps?.keyExtractor
              ? modalProps.keyExtractor
              : (product: PurchaseOrderProduct) => product.productId as unknown as string
          }
          data={filteredProducts}
          totalItems={totalItems}
          columns={overrideColumns || columns}
          namePage={translate("products")}
          currentPage={page}
          pageSize={pageSize}
          pageSizeList={totalItems > pageSize ? [10, 20, 40, 60] : []}
          rowActions={
            !modalProps?.modalView
              ? {
                  handleEdit: viewOnly ? undefined : (rowId) => editProduct(rowId),
                  handleDetails: (rowId) => viewProduct(rowId),
                }
              : undefined
          }
          rowActionsExtra={viewOnly || modalProps?.modalView ? undefined : rowActionsExtra}
          positionActionsColumn="right"
          generalActions={generalActions}
          onChangePageSize={handlePageSize}
          onNextPage={handleCurrentPage}
          onPreviousPage={handleCurrentPage}
          onGoToPage={handleCurrentPage}
          emptyData={translate("noProductsToShow")}
          showSelection={totalItems !== 0 && !viewOnly}
          showFooterGrid={totalItems !== 0}
          selecteds={modalProps?.selecteds}
          handleSelect={modalProps?.handleSelect}
        />
      </div>
      {renderAddProductModal()}
      {renderEditProductModal()}
      {renderCommentDialog()}
    </div>
  );
};

export { SkeletonProducts };
export default OrderProducts;
